import React, { useState } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Play, CheckSquare, Ban } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/context/AuthContext';
import { DecimalKeypad, TextKeypad } from '@/components/ui/keypads';

interface TransformationActionModalProps {
  isOpen: boolean;
  type: 'approve' | 'reject' | 'start' | 'complete' | 'cancel' | null;
  orderNumber: string;
  onClose: () => void;
  onConfirm: (data?: any) => void;
}

export const TransformationActionModal: React.FC<TransformationActionModalProps> = ({
  isOpen,
  type,
  orderNumber,
  onClose,
  onConfirm
}) => {
  const [reason, setReason] = useState('');
  const [actualQuantity, setActualQuantity] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useAuth();
  const isCajero = user?.positionId === 3;
  const [quantityKeypadOpen, setQuantityKeypadOpen] = useState(false);
  const [reasonKeypadOpen, setReasonKeypadOpen] = useState(false);

  const resetForm = () => {
    setReason('');
    setActualQuantity('');
    setIsSubmitting(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleConfirm = async () => {
    setIsSubmitting(true);
    
    try {
      const data: any = {};
      
      if (type === 'reject' || type === 'cancel') {
        data.reason = reason;
      }
      
      if (type === 'complete') {
        data.actualTargetQuantity = parseFloat(actualQuantity);
      }
      
      await onConfirm(data);
      handleClose();
    } catch (error) {
      console.error('Error in action:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getModalConfig = () => {
    switch (type) {
      case 'approve':
        return {
          title: 'Aprobar Transformación',
          description: 'Se aprobará la orden y quedará lista para ser ejecutada.',
          icon: <CheckCircle className="h-8 w-8 text-green-600" />,
          confirmText: 'Aprobar Orden',
          confirmColor: 'bg-green-600 hover:bg-green-700',
          alert: {
            type: 'success' as const,
            message: 'La orden será aprobada y el stock se reservará automáticamente.'
          }
        };
        
      case 'reject':
        return {
          title: 'Rechazar Transformación',
          description: 'La orden será devuelta al estado borrador para correcciones.',
          icon: <XCircle className="h-8 w-8 text-orange-600" />,
          confirmText: 'Rechazar y Devolver',
          confirmColor: 'bg-orange-600 hover:bg-orange-700',
          alert: {
            type: 'warning' as const,
            message: 'La orden regresará a borrador y el solicitante podrá editarla.'
          }
        };
        
      case 'start':
        return {
          title: 'Iniciar Transformación',
          description: 'Se iniciará el proceso físico de transformación de insumos.',
          icon: <Play className="h-8 w-8 text-blue-600" />,
          confirmText: 'Iniciar Proceso',
          confirmColor: 'bg-blue-600 hover:bg-blue-700',
          alert: {
            type: 'info' as const,
            message: 'El stock origen se descontará del inventario automáticamente.'
          }
        };
        
      case 'complete':
        return {
          title: 'Completar Transformación',
          description: 'Registre la cantidad real obtenida para finalizar la transformación.',
          icon: <CheckSquare className="h-8 w-8 text-green-600" />,
          confirmText: 'Completar y Registrar',
          confirmColor: 'bg-green-600 hover:bg-green-700',
          alert: {
            type: 'success' as const,
            message: 'Se calculará automáticamente la eficiencia del proceso.'
          }
        };
        
      case 'cancel':
        return {
          title: 'Cancelar Transformación',
          description: 'La orden será cancelada y no se podrá recuperar.',
          icon: <Ban className="h-8 w-8 text-red-600" />,
          confirmText: 'Cancelar Orden',
          confirmColor: 'bg-red-600 hover:bg-red-700',
          alert: {
            type: 'destructive' as const,
            message: 'El stock reservado será liberado automáticamente.'
          }
        };
        
      default:
        return null;
    }
  };

  const config = getModalConfig();
  if (!config || !isOpen) return null;

  const requiresReason = type === 'reject' || type === 'cancel';
  const requiresQuantity = type === 'complete';
  const canSubmit = !requiresReason || reason.trim() !== '';

  // Validación mejorada para cantidad: verificar que sea un número válido y mayor a 0
  const parsedQuantity = parseFloat(actualQuantity);
  const canSubmitComplete = !requiresQuantity ||
    (actualQuantity !== '' && !isNaN(parsedQuantity) && parsedQuantity > 0);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-3">
            {config.icon}
            <div>
              <DialogTitle className="text-xl">{config.title}</DialogTitle>
              <DialogDescription className="text-base">
                Orden: <strong>{orderNumber}</strong>
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-gray-700">{config.description}</p>
          
          <Alert className={
            config.alert.type === 'success' ? 'border-green-200 bg-green-50' :
            config.alert.type === 'warning' ? 'border-orange-200 bg-orange-50' :
            config.alert.type === 'info' ? 'border-blue-200 bg-blue-50' :
            'border-red-200 bg-red-50'
          }>
            <AlertTriangle className={
              `h-4 w-4 ${
                config.alert.type === 'success' ? 'text-green-600' :
                config.alert.type === 'warning' ? 'text-orange-600' :
                config.alert.type === 'info' ? 'text-blue-600' :
                'text-red-600'
              }`
            } />
            <AlertDescription className={
              config.alert.type === 'success' ? 'text-green-700' :
              config.alert.type === 'warning' ? 'text-orange-700' :
              config.alert.type === 'info' ? 'text-blue-700' :
              'text-red-700'
            }>
              {config.alert.message}
            </AlertDescription>
          </Alert>

          {requiresQuantity && (
            <div className="space-y-2">
              <Label htmlFor="actualQuantity">Cantidad Real Obtenida *</Label>
              <Input
                id="actualQuantity"
                type={isCajero ? 'text' : 'number'}
                min={isCajero ? undefined : '0'}
                step={isCajero ? undefined : '0.01'}
                value={actualQuantity}
                onChange={!isCajero ? (e) => setActualQuantity(e.target.value) : undefined}
                readOnly={isCajero}
                onClick={isCajero ? () => setQuantityKeypadOpen(true) : undefined}
                placeholder="Ej: 4.2"
                className={`text-center ${isCajero ? 'cursor-pointer' : ''}`}
              />
              <p className="text-xs text-gray-500">
                Ingrese la cantidad real obtenida del proceso de transformación
              </p>
            </div>
          )}

          {requiresReason && (
            <div className="space-y-2">
              <Label htmlFor="reason">
                {type === 'reject' ? 'Motivo del Rechazo' : 'Motivo de Cancelación'}
                {type === 'cancel' && ' *'}
              </Label>
              <Textarea
                id="reason"
                value={reason}
                onChange={!isCajero ? (e) => setReason(e.target.value) : undefined}
                readOnly={isCajero}
                onClick={isCajero ? () => setReasonKeypadOpen(true) : undefined}
                placeholder={
                  type === 'reject'
                    ? 'Especifique las correcciones necesarias...'
                    : 'Explique el motivo de la cancelación...'
                }
                rows={3}
                className={isCajero ? 'cursor-pointer' : ''}
              />
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button 
            variant="outline" 
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            className={config.confirmColor}
            onClick={handleConfirm}
            disabled={
              isSubmitting || 
              (requiresReason && !canSubmit) || 
              (requiresQuantity && !canSubmitComplete)
            }
          >
            {isSubmitting ? 'Procesando...' : config.confirmText}
          </Button>
        </div>
        {/* Keypads para cajero */}
        {isCajero && (
          <>
            <DecimalKeypad
              isOpen={quantityKeypadOpen}
              onClose={() => setQuantityKeypadOpen(false)}
              onSubmit={(value) => {
                setActualQuantity(value);
                setQuantityKeypadOpen(false);
              }}
              title="Cantidad Real Obtenida"
              initialValue={actualQuantity || '0'}
            />
            <TextKeypad
              isOpen={reasonKeypadOpen}
              onClose={() => setReasonKeypadOpen(false)}
              onSubmit={(value) => {
                setReason(value);
                setReasonKeypadOpen(false);
              }}
              title={type === 'reject' ? 'Motivo del Rechazo' : 'Motivo de Cancelación'}
              initialValue={reason}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};